/**
 * Reportes → Faltante del personal (`/reportes/faltantes-personal`).
 *
 * Puerto de la ficha *Reporte faltante del personal por mes* (`Reportes::missing` →
 * `views/missing/index.php`) y su AJAX `turno/get_data_missing` +
 * `Reportes_model::get_faltante_personal_rango_fecha` + `Reportes_model::getFaltanteTurno`.
 *
 * La pantalla del panel anterior pedía **fecha de inicio** (por defecto el día 1 del
 * mes), **fecha de fin** (hoy) y **trabajador**, y listaba los turnos con su **faltante
 * verificado**: fecha, turno, trabajador, si el turno estaba validado y el monto que
 * quedó pendiente de entregar, con el total en el pie y exports a Excel, PDF, CSV e
 * impresión.
 *
 * Reglas conservadas:
 * - El **faltante** es el `monto_esperado` de la verificación del turno
 *   (`verificacion` con `estado = 1`), que es la diferencia de la liquidación que el
 *   trabajador dejó pendiente; un turno **sin verificar** no tiene faltante.
 * - El rango va por `turno.hora_llegada` y el trabajador es el del turno.
 *
 * Diferencias anotadas:
 * - Se añaden las tarjetas del periodo, el reparto por **trabajador** y por **mes** (la
 *   ficha es «por mes») y el CSV/PDF los sirve la API.
 * - El «ingreso a bóveda» se lee de la tabla `boveda` (el turno guardaba esa cifra en
 *   una columna que ya no se mantiene y salía siempre en cero).
 */

import { ArrowLeft, Download, FileText, Info, RefreshCw, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Paginador } from '@/components/ui/paginador';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateValue, formatDateTimeValue, formatNumber } from '@/lib/utils';

import {
  reportesApi,
  useCatalogosFaltantesPersonal,
  useFaltantesPersonal,
  type FiltrosFaltantesPersonal,
} from '../reportes.api';

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Fecha local `AAAA-MM-DD` (sin desfasar por la zona horaria). */
function fechaLocal(fecha: Date): string {
  return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(
    fecha.getDate(),
  ).padStart(2, '0')}`;
}

/** Rango por defecto del reporte: el mes en curso (como el original). */
function rangoPorDefecto(): FiltrosFaltantesPersonal {
  const hoy = new Date();

  return {
    desde: fechaLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 1)),
    hasta: fechaLocal(hoy),
    usuario: '',
  };
}

/** «agosto 2026» a partir de `2026-08`. */
function etiquetaMes(periodo: string): string {
  const [anio, mes] = periodo.split('-');
  const nombre = MESES[Number(mes) - 1];

  return nombre ? `${nombre} ${anio}` : periodo;
}

export function ReporteFaltantesPersonalPage() {
  useDocumentTitle('Reportes · Faltante del personal');

  const [filtros, setFiltros] = useState<FiltrosFaltantesPersonal>(rangoPorDefecto);
  const [aviso, setAviso] = useState<string | null>(null);

  const catalogos = useCatalogosFaltantesPersonal();
  const consulta = useFaltantesPersonal(filtros);
  const datos = consulta.data;
  const resumen = datos?.resumen;

  const filas = useMemo(() => datos?.filas ?? [], [datos]);
  // El listado se pagina en el navegador; los repartos y el pie siguen usando el listado
  // completo.
  const paginacion = usePaginacion(filas, { clave: filtros });
  const porTrabajador = datos?.por_trabajador ?? [];
  const porMes = datos?.por_mes ?? [];
  const trabajadores = catalogos.data?.trabajadores ?? [];
  const meses = catalogos.data?.meses ?? [];

  const cambiar = <C extends keyof FiltrosFaltantesPersonal>(
    campo: C,
    valor: FiltrosFaltantesPersonal[C],
  ) => setFiltros((previos) => ({ ...previos, [campo]: valor }));

  const exportar = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.exportarFaltantesPersonal(filtros), {
        nombre: `faltantes-personal-${filtros.desde}_${filtros.hasta}.csv`,
      });
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo exportar el reporte.');
    }
  };

  const exportarPdf = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.pdfFaltantesPersonal(filtros));
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo generar el PDF.');
    }
  };

  const rangoDe = (mes: string) => {
    const anio = Number(mes.slice(0, 4));
    const numero = Number(mes.slice(5, 7));

    return { desde: `${mes}-01`, hasta: fechaLocal(new Date(anio, numero, 0)) };
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">Faltante del personal</h1>
          <p className="mt-1 text-sm text-ink-500">
            Lo que quedó pendiente de entregar en cada turno verificado, por trabajador y por mes.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={exportarPdf}
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
          >
            PDF
          </Button>

          <Button
            variant="outline"
            onClick={exportar}
            leftIcon={<Download className="size-4" aria-hidden="true" />}
          >
            Exportar
          </Button>
          <Button
            variant="outline"
            onClick={() => void consulta.refetch()}
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          >
            Actualizar
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Desde</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.desde}
                  onChange={(evento) => cambiar('desde', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Hasta</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.hasta}
                  onChange={(evento) => cambiar('hasta', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm sm:col-span-2">
              <span className="font-medium text-ink-700">Trabajador</span>
              <Select
                value={filtros.usuario}
                onChange={({ target }) => cambiar('usuario', target.value)}
                className={CONTROL}
                aria-label="Trabajador"
              >
                <option value="">TODOS</option>
                {trabajadores.map((trabajador) => (
                  <option key={trabajador.id} value={String(trabajador.id)}>
                    {trabajador.trabajador} ({formatNumber(trabajador.turnos)})
                  </option>
                ))}
              </Select>
            </label>
          </div>

          {meses.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 border-t border-ink-100 pt-3">
              <span className="mr-1 inline-flex items-center gap-1.5 text-xs font-semibold text-ink-500">
                <Search className="size-3.5" aria-hidden="true" />
                Meses con turnos
              </span>
              {meses.slice(0, 12).map((mes) => {
                const rango = rangoDe(mes.periodo);
                const activo = rango.desde === filtros.desde && rango.hasta === filtros.hasta;

                return (
                  <button
                    key={mes.periodo}
                    type="button"
                    aria-pressed={activo}
                    onClick={() => setFiltros((previos) => ({ ...previos, ...rango }))}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                      activo
                        ? 'border-brand-300 bg-brand-50 text-brand-700'
                        : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                    )}
                    title={`${formatNumber(mes.turnos)} turno(s)`}
                  >
                    {etiquetaMes(mes.periodo)}
                  </button>
                );
              })}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo cargar el reporte">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="error" title="No se pudo exportar">
          {aviso}
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        {[
          {
            etiqueta: 'Turnos',
            valor: formatNumber(resumen?.turnos ?? 0),
            detalle: `${formatNumber(resumen?.dias ?? 0)} día(s)`,
          },
          {
            etiqueta: 'Verificados',
            valor: formatNumber(resumen?.validadas ?? 0),
            detalle: `${formatNumber(resumen?.sin_verificar ?? 0)} sin verificar`,
          },
          {
            etiqueta: 'Faltante',
            valor: formatCurrency(resumen?.faltante ?? 0),
            detalle: `${formatNumber(resumen?.con_faltante ?? 0)} turno(s) con faltante`,
          },
          {
            etiqueta: 'Ingreso a bóveda',
            valor: formatCurrency(resumen?.ingreso_boveda ?? 0),
            detalle: 'de los turnos del rango',
          },
          {
            etiqueta: 'Trabajadores',
            valor: formatNumber(resumen?.trabajadores ?? 0),
            detalle: 'con turnos en el rango',
          },
        ].map((tarjeta) => (
          <Card key={tarjeta.etiqueta}>
            <CardContent className="p-4">
              <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                {tarjeta.etiqueta}
              </p>
              <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
              <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card className="min-w-0">
          <CardContent className="space-y-3 p-4">
            <h2 className="text-sm font-semibold text-ink-800">Por trabajador</h2>

            {consulta.isLoading ? <Skeleton className="h-32 w-full" /> : null}

            {!consulta.isLoading ? (
              <div className="max-h-[40vh] overflow-auto rounded-card border border-ink-100">
                <table className="w-full border-collapse text-[13px]">
                  <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                    <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                      <th className="px-2 py-2">Trabajador</th>
                      <th className="w-[4.5rem] px-2 py-2 text-right">Turnos</th>
                      <th className="w-[5rem] px-2 py-2 text-right">Verific.</th>
                      <th className="w-[6.5rem] px-2 py-2 text-right">Faltante</th>
                    </tr>
                  </thead>
                  <tbody>
                    {porTrabajador.length === 0 ? (
                      <tr className="border-t border-ink-100">
                        <td colSpan={4} className="px-2 py-8 text-center text-sm text-ink-500">
                          No hay turnos que cumplan los filtros.
                        </td>
                      </tr>
                    ) : null}

                    {porTrabajador.map((fila) => (
                      <tr
                        key={fila.usuario_id}
                        className="border-t border-ink-100 hover:bg-brand-50/30"
                      >
                        <td className="px-2 py-2">
                          <p className="truncate font-semibold text-ink-900" title={fila.trabajador ?? ''}>
                            {fila.trabajador ?? '—'}
                          </p>
                          <p className="truncate text-[11px] text-ink-500">{fila.dni ?? ''}</p>
                        </td>
                        <td className="px-2 py-2 text-right text-ink-700">
                          {formatNumber(fila.turnos)}
                        </td>
                        <td className="px-2 py-2 text-right text-ink-700">
                          {formatNumber(fila.validadas)}
                        </td>
                        <td
                          className={cn(
                            'px-2 py-2 text-right font-semibold',
                            fila.faltante > 0 ? 'text-rose-700' : 'text-ink-900',
                          )}
                        >
                          {formatCurrency(fila.faltante)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card className="min-w-0">
          <CardContent className="space-y-3 p-4">
            <h2 className="text-sm font-semibold text-ink-800">Por mes</h2>

            {consulta.isLoading ? <Skeleton className="h-32 w-full" /> : null}

            {!consulta.isLoading ? (
              <div className="max-h-[40vh] overflow-auto rounded-card border border-ink-100">
                <table className="w-full border-collapse text-[13px]">
                  <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                    <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                      <th className="px-2 py-2">Mes</th>
                      <th className="w-[4.5rem] px-2 py-2 text-right">Turnos</th>
                      <th className="w-[5rem] px-2 py-2 text-right">Verific.</th>
                      <th className="w-[6.5rem] px-2 py-2 text-right">Faltante</th>
                    </tr>
                  </thead>
                  <tbody>
                    {porMes.length === 0 ? (
                      <tr className="border-t border-ink-100">
                        <td colSpan={4} className="px-2 py-8 text-center text-sm text-ink-500">
                          No hay turnos que cumplan los filtros.
                        </td>
                      </tr>
                    ) : null}

                    {porMes.map((fila) => (
                      <tr key={fila.periodo} className="border-t border-ink-100 hover:bg-brand-50/30">
                        <td className="px-2 py-2 font-semibold text-ink-900">
                          {etiquetaMes(fila.periodo)}
                        </td>
                        <td className="px-2 py-2 text-right text-ink-700">
                          {formatNumber(fila.turnos)}
                        </td>
                        <td className="px-2 py-2 text-right text-ink-700">
                          {formatNumber(fila.validadas)}
                        </td>
                        <td
                          className={cn(
                            'px-2 py-2 text-right font-semibold',
                            fila.faltante > 0 ? 'text-rose-700' : 'text-ink-900',
                          )}
                        >
                          {formatCurrency(fila.faltante)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-ink-800">
              Turnos del periodo ({formatNumber(resumen?.turnos ?? 0)})
            </h2>
            <span className="text-xs text-ink-500">
              {formatDateValue(filtros.desde)} — {formatDateValue(filtros.hasta)} · el rango va por la
              llegada del turno
            </span>
          </div>

          {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

          {!consulta.isLoading ? (
            <>
            <div className="max-h-[60vh] overflow-auto rounded-card border border-ink-100">
              <table className="w-full max-lg:min-w-[44rem] table-fixed border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="w-[5.25rem] px-1.5 py-2">Fecha</th>
                    <th className="w-[3.5rem] px-1.5 py-2">Turno</th>
                    <th className="px-1.5 py-2">Trabajador</th>
                    <th className="w-[6.5rem] px-1.5 py-2">Llegada</th>
                    <th className="w-[6.5rem] px-1.5 py-2">Salida</th>
                    <th className="w-[6rem] px-1.5 py-2 text-right">Bóveda</th>
                    <th className="w-[7rem] px-1.5 py-2">Validación</th>
                    <th className="w-[5.5rem] px-1.5 py-2 text-right">Faltante</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={8} className="px-1.5 py-10 text-center text-sm text-ink-500">
                        No hay turnos que cumplan los filtros.
                      </td>
                    </tr>
                  ) : null}

                  {paginacion.visibles.map((fila) => (
                    <tr key={fila.turno_id} className="border-t border-ink-100 hover:bg-brand-50/30">
                      <td className="px-1.5 py-2 text-ink-700">{formatDateValue(fila.fecha)}</td>
                      <td className="px-1.5 py-2 text-ink-600">{fila.turno_id}</td>
                      <td className="px-1.5 py-2">
                        <p className="truncate font-semibold text-ink-900" title={fila.trabajador ?? ''}>
                          {fila.trabajador ?? '—'}
                        </p>
                        <p className="truncate text-[11px] text-ink-500">{fila.dni ?? ''}</p>
                      </td>
                      <td className="px-1.5 py-2 text-ink-700">{formatDateTimeValue(fila.llegada)}</td>
                      <td className="px-1.5 py-2 text-ink-700">{formatDateTimeValue(fila.salida)}</td>
                      <td className="px-1.5 py-2 text-right text-ink-700">
                        {formatCurrency(fila.ingreso_boveda)}
                      </td>
                      <td className="px-1.5 py-2">
                        {fila.validado ? (
                          <>
                            <Badge variant="success" className="px-1.5 py-0 text-[10px]">
                              Verificado
                            </Badge>
                            <p className="truncate text-[11px] text-ink-500">
                              {formatDateTimeValue(fila.fecha_verificacion)}
                            </p>
                          </>
                        ) : (
                          <Badge variant="warning" className="px-1.5 py-0 text-[10px]">
                            Sin verificar
                          </Badge>
                        )}
                        {fila.detalle ? (
                          <p className="truncate text-[11px] text-ink-400" title={fila.detalle}>
                            {fila.detalle}
                          </p>
                        ) : null}
                      </td>
                      <td
                        className={cn(
                          'px-1.5 py-2 text-right font-semibold',
                          (fila.faltante ?? 0) > 0 ? 'text-rose-700' : 'text-ink-900',
                        )}
                      >
                        {fila.faltante === null ? '—' : formatCurrency(fila.faltante)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {filas.length > 0 ? (
                  <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_0_var(--color-ink-100)]">
                    <tr className="text-[13px] font-semibold text-ink-800">
                      <td className="px-1.5 py-2" colSpan={5}>
                        TOTAL · {formatNumber(resumen?.turnos ?? 0)} turno(s)
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        {formatCurrency(resumen?.ingreso_boveda ?? 0)}
                      </td>
                      <td className="px-1.5 py-2">
                        <span className="text-[11px] font-normal text-ink-500">
                          {formatNumber(resumen?.validadas ?? 0)} verificado(s) ·{' '}
                          {formatNumber(resumen?.sin_verificar ?? 0)} sin verificar
                        </span>
                      </td>
                      <td className="px-1.5 py-2 text-right text-rose-700">
                        {formatCurrency(resumen?.faltante ?? 0)}
                      </td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>

            <Paginador {...paginacion} etiqueta="turnos" />
            </>
          ) : null}
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <span>
          El <strong>faltante</strong> es el monto que quedó pendiente de entregar según la
          verificación del turno (`verificacion.monto_esperado`): un turno sin verificar no tiene
          faltante (se ve como «Sin verificar»). El rango va por la llegada del turno y el trabajador
          es el del turno, no quien lo verificó.
        </span>
      </p>
    </div>
  );
}
